import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Badge,
  FormGroup,
  Label,
  Card,
  CardBody,
  Row,
  Col,
  Spinner,
} from "reactstrap";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/flatpickr.css";
import dayjs from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DesktopTimePicker } from "@mui/x-date-pickers/DesktopTimePicker";
import { useDispatch } from "react-redux";
import { fetchAvailableFarms, addNewBooking } from "../../slices/bookings/thunk";
import { getAllUsers, createAdminUser, updateUser } from "../../helpers/apis";
import { showToast } from "../../slices/toast/reducer";

/**
 * Reusable create booking modal used by Bookings and Users pages.
 * Props:
 *  - isOpen: boolean to control visibility
 *  - onClose: function to close modal
 *  - skipUserStep: if true, hides step 2 (user selection)
  *  - preselectedUser: user object to pre-fill when skipUserStep is true
 *  - onBookingCreated: callback invoked after successful creation
 */
const CreateBookingModal = ({
  isOpen,
  onClose,
  skipUserStep = false,
  preselectedUser = null,
  onBookingCreated,
}) => {
  const dispatch = useDispatch();

  // Local state
  const [availableFarmhouses, setAvailableFarmhouses] = useState([]);
  const [createStep, setCreateStep] = useState(1);
  const [createDate, setCreateDate] = useState("");
  const [createEndDate, setCreateEndDate] = useState("");
  const [createTimeFrom, setCreateTimeFrom] = useState("07:00");
  const [createTimeTo, setCreateTimeTo] = useState("18:00");
  const [selectedFarmhouseId, setSelectedFarmhouseId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [newUser, setNewUser] = useState({ name: "", email: "", mobileNo: "" });
  const [creatingUser, setCreatingUser] = useState(false);
  const [bookingFormData, setBookingFormData] = useState({
    paidAmount: 0,
    remainingAmount: 0,
    paymentStatus: "incomplete",
  });
  const [createUserModalOpen, setCreateUserModalOpen] = useState(false);
  const [editUserModalOpen, setEditUserModalOpen] = useState(false);
  const [updatingUser, setUpdatingUser] = useState(false);
  const [editUserData, setEditUserData] = useState({ id: "", name: "", email: "", mobileNo: "" });
  const [submitting, setSubmitting] = useState(false);
  const checkInButtonRef = useRef(null);
  const checkOutButtonRef = useRef(null);
  const [checkInPickerOpen, setCheckInPickerOpen] = useState(false);
  const [checkOutPickerOpen, setCheckOutPickerOpen] = useState(false);

  const totalSteps = skipUserStep ? 2 : 3;

  const isWeekend = (dateString) => {
    if (!dateString) return false;
    const date = new Date(dateString);
    const day = date.getDay();
    return day === 0 || day === 6;
  };

  const getBookingTypeLabel = (type) => {
    switch (type) {
      case "HOURLY_BOOKING":
        return "Hourly booking";
      case "REGULAR_12HR":
        return "Regular 12H (7 AM - 6 PM)";
      case "REGULAR_24HR":
        return "Regular 24H (7 PM - 6 PM Next Day)";
      case "WEEKEND_12HR":
        return "Weekend 12H (7 AM - 6 PM)";
      case "WEEKEND_24HR":
        return "Weekend 24H (7 PM - 6 PM Next Day)";
      default:
        return type || "Regular 12H";
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return "7:00 AM";
    try {
      const [hours, minutes] = timeString.split(":");
      const h = parseInt(hours, 10);
      const ampm = h >= 12 ? "PM" : "AM";
      const h12 = h % 12 || 12;
      return `${h12}:${minutes} ${ampm}`;
    } catch {
      return timeString;
    }
  };

  const formatBookingDate = (dateString) => {
    if (!dateString) return "-";
    try {
      return new Date(dateString).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  const calculateEndDateForCreate = (startDate, type) => {
    if ((type === "REGULAR_24HR" || type === "WEEKEND_24HR") && startDate) {
      const date = new Date(startDate);
      date.setDate(date.getDate() + 1);
      return date.toISOString().split("T")[0];
    }
    return startDate;
  };

  const formatTimeForQuery = (timeString) => {
    if (!timeString) return "";
    try {
      // Convert "07:00" -> "7:00" (backend examples usually use no leading zero)
      const parts = String(timeString).split(":");
      if (parts.length < 2) return String(timeString);
      const hour = parseInt(parts[0], 10);
      const minute = parts[1]?.substring(0, 2);
      if (Number.isNaN(hour) || !minute) return String(timeString);
      return `${hour}:${minute}`;
    } catch {
      return String(timeString);
    }
  };

  const getBookingTypeFromTimes = (startDate, endDate, startTime, endTime) => {
    if (!startDate) return "REGULAR_12HR";
    const typePrefix = isWeekend(startDate) ? "WEEKEND" : "REGULAR";
    try {
      const start = new Date(`${startDate}T${startTime || "07:00"}`);
      const end = new Date(`${endDate || startDate}T${endTime || "18:00"}`);
      const durationMs = end - start;
      const actualHours = durationMs > 0 ? durationMs / (1000 * 60 * 60) : 0;
      const derivedType = actualHours > 12 ? "24HR" : "12HR";
      return `${typePrefix}_${derivedType}`;
    } catch {
      return `${typePrefix}_12HR`;
    }
  };

  const calculateDurationAndRent = useCallback((startDate, endDate, startTime, endTime, farmhouse) => {
    if (!startDate || !endDate || !farmhouse) return { hours: 0, rent: 0 };
    try {
      const start = new Date(`${startDate}T${startTime || "07:00"}`);
      const end = new Date(`${endDate}T${endTime || "18:00"}`);
      const durationMs = end - start;
      const actualHours = Math.round(durationMs / (1000 * 60 * 60));
      if (actualHours <= 0) return { hours: 0, rent: 0 };
      const isWknd = isWeekend(startDate);
      const typePrefix = isWknd ? "WEEKEND" : "REGULAR";
      const price12h = farmhouse.priceOptions?.find((o) => o.category === `${typePrefix}_12HR`)?.price || farmhouse.price || 0;
      const price24h = farmhouse.priceOptions?.find((o) => o.category === `${typePrefix}_24HR`)?.price || (price12h * 1.8);
      let totalRent = 0;
      let billingHours = 0;
      if (actualHours <= 12) {
        billingHours = 12;
        totalRent = price12h;
      } else if (actualHours <= 24) {
        billingHours = 24;
        totalRent = price24h;
      } else {
        // Use 12-hour increments for stays over 24 hours
        billingHours = Math.ceil(actualHours / 12) * 12;

        const fullDays = Math.floor(actualHours / 24);
        const remainingHours = actualHours % 24;

        totalRent = fullDays * price24h;
        if (remainingHours > 0) {
          if (remainingHours <= 12) {
            totalRent += price12h;
          } else {
            totalRent += price24h;
          }
        }
      }
      return { hours: billingHours, rent: totalRent };
    } catch (e) {
      return { hours: 0, rent: 0 };
    }
  }, []);

  const fetchAvailableFarmsWithTimeFilters = (overrides = {}) => {
    const checkInDate = overrides.checkInDate ?? createDate;
    const checkOutDate = overrides.checkOutDate ?? createEndDate;
    const checkInFrom = overrides.checkInFrom ?? createTimeFrom;
    const checkOutTo = overrides.checkOutTo ?? createTimeTo;

    if (!checkInDate || !checkOutDate || !checkInFrom || !checkOutTo) return;

    const bookingTypeFromTimes = getBookingTypeFromTimes(
      checkInDate,
      checkOutDate,
      checkInFrom,
      checkOutTo
    );
    const bookingType = overrides.bookingType ?? bookingTypeFromTimes;

    const params = {
      // Backwards compatible params (older backend may rely on these)
      date: checkInDate,
      bookingType,
      // New time-slot filters (matches your curl example)
      checkInDate,
      checkOutDate,
      checkInFrom: formatTimeForQuery(checkInFrom),
      checkOutTo: formatTimeForQuery(checkOutTo),
    };

    dispatch(fetchAvailableFarms(params))
      .unwrap()
      .then((response) => {
        const farms = response?.data?.availableFarms || [];
        setAvailableFarmhouses(farms);
        setSelectedFarmhouseId(null);
      })
      .catch(() => setAvailableFarmhouses([]));
  };

  const resetForm = useCallback(
    (user) => {
      const today = new Date().toISOString().split("T")[0];
      const initialType = isWeekend(today) ? "WEEKEND_12HR" : "REGULAR_12HR";

      setCreateStep(1);
      setCreateDate(today);
      setCreateEndDate(today);
      setCreateTimeFrom("07:00");
      setCreateTimeTo("18:00");
      setSelectedFarmhouseId(null);
      setSearchTerm("");
      setUsers(user ? [user] : []);
      setSelectedUserId(user?.id || null);
      setNewUser({ name: "", email: "", mobileNo: "" });
      setBookingFormData({
        paidAmount: 0,
        remainingAmount: 0,
        paymentStatus: "incomplete",
        userId: user?.id || null,
      });

      // Load farms with time-slot filters for the initial selection
      fetchAvailableFarmsWithTimeFilters({
        checkInDate: today,
        checkOutDate: today,
        checkInFrom: "07:00",
        checkOutTo: "18:00",
        bookingType: initialType,
      });

      if (!skipUserStep) {
        fetchUsersForSelection();
      }
    },
    [dispatch, skipUserStep]
  );

  useEffect(() => {
    if (isOpen) {
      resetForm(preselectedUser || null);
    }
  }, [isOpen, preselectedUser, resetForm]);

  const fetchUsersForSelection = async (search = "") => {
    try {
      const params = search ? { search } : {};
      const response = await getAllUsers(params);
      const usersList = response?.data?.users || response?.data || [];
      setUsers(Array.isArray(usersList) ? usersList : []);
    } catch (error) {
      console.error("Failed to fetch users:", error);
      setUsers([]);
    }
  };

  const handleOpenEditUser = (user) => {
    setEditUserData({
      id: user.id || user._id,
      name: user.name || "",
      email: user.email || "",
      mobileNo: user.mobileNo || user.mobile || ""
    });
    setEditUserModalOpen(true);
  };

  const buildUserPayload = (data) => {
    const payload = {
      name: (data?.name || "").trim(),
      mobileNo: (data?.mobileNo || "").trim(),
    };

    const email = (data?.email || "").trim();
    if (email) payload.email = email;

    return payload;
  };

  const handleUpdateUser = async () => {
    if (!editUserData.name) {
      dispatch(showToast({ type: "warning", msg: "Name is required" }));
      return;
    }

    setUpdatingUser(true);
    try {
      const response = await updateUser(editUserData.id, buildUserPayload(editUserData));

      if (response.error) {
        throw new Error(response.msg || "Failed to update user");
      }

      dispatch(showToast({ type: "success", msg: "User updated successfully" }));

      // Update local users list
      setUsers(prev => prev.map(u =>
        (u.id === editUserData.id || u._id === editUserData.id)
          ? { ...u, ...editUserData }
          : u
      ));

      setEditUserModalOpen(false);
    } catch (error) {
      console.error("Update user error:", error);
      dispatch(showToast({ type: "error", msg: error.message || "Failed to update user" }));
    } finally {
      setUpdatingUser(false);
    }
  };

  const handleCreateNewUser = async () => {
    if (!newUser.name) {
      dispatch(showToast({ type: "warning", msg: "Please enter name" }));
      return;
    }

    setCreatingUser(true);
    try {
      const userData = buildUserPayload(newUser);

      const response = await createAdminUser(userData);
      const createdUser = response.data || response;

      setUsers((prev) => [...prev, createdUser]);

      setSelectedUserId(createdUser.id);
      setBookingFormData((prev) => ({
        ...prev,
        userId: createdUser.id,
      }));

      setNewUser({ name: "", email: "", mobileNo: "" });
      setCreateUserModalOpen(false);

      dispatch(showToast({ type: "success", msg: "Customer created successfully" }));
    } catch (error) {
      console.error("Failed to create user:", error);
      dispatch(showToast({ type: "error", msg: error.message || "Failed to create customer" }));
    } finally {
      setCreatingUser(false);
    }
  };

  const handleStep1Next = () => {
    if (!createDate) {
      dispatch(showToast({ type: "warning", msg: "Please select booking date range" }));
      return;
    }
    if (!createEndDate) {
      dispatch(showToast({ type: "warning", msg: "Please select booking end date" }));
      return;
    }
    if (!createTimeFrom || !createTimeTo) {
      dispatch(showToast({ type: "warning", msg: "Please select check-in and check-out times" }));
      return;
    }
    if (!selectedFarmhouseId) {
      dispatch(showToast({ type: "warning", msg: "Please select a farmhouse" }));
      return;
    }

    const selectedFarm = availableFarmhouses.find((farm) => farm.id === selectedFarmhouseId);
    if (!selectedFarm) {
      dispatch(showToast({ type: "warning", msg: "Selected farmhouse not found" }));
      return;
    }

    const endDate = createEndDate;

    const { rent, hours } = calculateDurationAndRent(createDate, endDate, createTimeFrom, createTimeTo, selectedFarm);
    const basePrice = rent || selectedFarm.price || 0;
    const derivedType = (hours > 12 ? "24HR" : "12HR");
    const typePrefix = isWeekend(createDate) ? "WEEKEND" : "REGULAR";
    const bookingTypeDerived = `${typePrefix}_${derivedType}`;
    const priceOption = selectedFarm.priceOptions?.find((o) => o.category === bookingTypeDerived);
    const maxPersonsForType = priceOption?.maxPeople || selectedFarm.maxPersons || 10;
    const extraPersonPrice = priceOption?.extraPersonPrice || 0;

    setBookingFormData((prev) => ({
      ...prev,
      farmhouseId: selectedFarmhouseId,
      bookingDate: createDate,
      bookingEndDate: endDate,
      bookingType: bookingTypeDerived,
      bookingTimeFrom: createTimeFrom,
      bookingTimeTo: createTimeTo,
      numberOfPersons: maxPersonsForType,
      originalPrice: basePrice,
      extraPersonCharge: 0,
      extraPersonPrice: extraPersonPrice,
      discountAmount: 0,
      finalPrice: basePrice,
      paymentStatus: "paid",
      farmStatus: "available",
      isLoggedIn: false,
      farmMaxPersons: maxPersonsForType,
      farmPriceOption: priceOption,
      paidAmount: basePrice,
      remainingAmount: 0,
      bookingHours: hours,
    }));

    setCreateStep(skipUserStep ? 2 : 2);
  };

  const handleStep2Next = () => {
    if (!selectedUserId) {
      dispatch(showToast({ type: "warning", msg: "Please select or create a customer" }));
      return;
    }

    const selectedUser = users.find((user) => user.id === selectedUserId);
    if (!selectedUser) return;

    setBookingFormData((prev) => ({
      ...prev,
      userId: selectedUser.id,
    }));

    setCreateStep(3);
  };

  const handleCreateBooking = async () => {
    if (!bookingFormData.userId || !bookingFormData.farmhouseId || !bookingFormData.bookingDate) {
      dispatch(showToast({ type: "warning", msg: "Please complete all required fields" }));
      return;
    }

    const bookingPayload = {
      farmhouseId: bookingFormData.farmhouseId,
      userId: bookingFormData.userId,
      bookingDate: bookingFormData.bookingDate,
      bookingEndDate: bookingFormData.bookingEndDate || bookingFormData.bookingDate,
      bookingTimeFrom: bookingFormData.bookingTimeFrom || "10:00",
      bookingTimeTo: bookingFormData.bookingTimeTo || "22:00",
      numberOfPersons: bookingFormData.numberOfPersons || 1,
      bookingType: "HOURLY_BOOKING",
      originalPrice: bookingFormData.originalPrice || 0,
      discountAmount: bookingFormData.discountAmount || 0,
      finalPrice: bookingFormData.finalPrice || bookingFormData.originalPrice || 0,
      isLoggedIn: false,
      paymentStatus: bookingFormData.paymentStatus || "incomplete",
      farmStatus: "available",
      bookingHours: (bookingFormData.bookingHours || 12).toString(),
      bookingData: {
        extraPersonCharge: bookingFormData.extraPersonCharge || 0,
        extraPersonPrice: bookingFormData.extraPersonPrice || 0,
      },
    };

    if (bookingFormData.paymentStatus === "partial") {
      const total = bookingFormData.finalPrice || bookingFormData.originalPrice || 0;
      const paidAmount = bookingFormData.paidAmount || Math.floor(total / 2);
      const remainingAmount = bookingFormData.remainingAmount || total - paidAmount;

      bookingPayload.paidAmount = paidAmount;
      bookingPayload.remainingAmount = remainingAmount;
      bookingPayload.notes = "Partial payment on booking creation";
    } else if (bookingFormData.paymentStatus === "paid") {
      const total = bookingFormData.finalPrice || bookingFormData.originalPrice || 0;
      bookingPayload.paidAmount = total;
      bookingPayload.remainingAmount = 0;
    } else if (bookingFormData.paymentStatus === "incomplete") {
      const total = bookingFormData.finalPrice || bookingFormData.originalPrice || 0;
      bookingPayload.paidAmount = 0;
      bookingPayload.remainingAmount = total;
    }

    setSubmitting(true);
    try {
      await dispatch(addNewBooking(bookingPayload)).unwrap();
      dispatch(
        showToast({
          type: "success",
          msg: `Booking created successfully${bookingFormData.paymentStatus === "partial" ? ` (₹${bookingFormData.paidAmount || 0} paid)` : ""
            }`,
        })
      );
      onClose?.();
      resetForm(preselectedUser || null);
      if (onBookingCreated) onBookingCreated();
    } catch (error) {
      console.error("Failed to create booking:", error);
      dispatch(showToast({ type: "error", msg: error.message || "Failed to create booking" }));
    } finally {
      setSubmitting(false);
    }
  };

  const renderSelectedCustomer = useMemo(() => {
    const selectedUser = users.find((user) => user.id === bookingFormData.userId);
    if (selectedUser) return selectedUser;
    if (preselectedUser && bookingFormData.userId === preselectedUser.id) {
      return preselectedUser;
    }
    return null;
  }, [users, bookingFormData.userId, preselectedUser]);

  if (!isOpen) return null;

  return (
    <>
      <Modal isOpen={isOpen} toggle={onClose} size="xl" centered className="full-height-model">
        <ModalHeader toggle={onClose}>
          <i className="ri-add-circle-line me-2"></i>
          Create New Booking
          <Badge color="light" className="ms-2 text-black">
            Step {createStep} of {totalSteps}
          </Badge>
        </ModalHeader>

        <ModalBody>
          {createStep === 1 && (
            <div>
              <h6 className="mb-3 fw-semibold text-primary">
                <i className="ri-home-4-line me-2"></i>
                Select Farmhouse & Date
              </h6>

              <Row>
                <Col md={6}>
                  <FormGroup className="mb-3">
                    <Label className="fw-medium">Check-in Date *</Label>
                    <Flatpickr
                      className="form-control d-block"
                      options={{
                        dateFormat: "d M, Y",
                        disableMobile: true,
                      }}
                      value={createDate ? new Date(createDate + "T00:00:00") : ""}
                      onChange={(dates) => {
                        if (dates.length > 0) {
                          const d = dates[0];
                          const startDateString =
                            d.getFullYear() +
                            "-" +
                            String(d.getMonth() + 1).padStart(2, "0") +
                            "-" +
                            String(d.getDate()).padStart(2, "0");
                          setCreateDate(startDateString);

                          const endDateToUse =
                            createEndDate && createEndDate < startDateString ? startDateString : (createEndDate || startDateString);

                          if (createEndDate && createEndDate < startDateString) {
                            setCreateEndDate(startDateString);
                          }

                          // Fetch available farms for the selected time-slot
                          fetchAvailableFarmsWithTimeFilters({
                            checkInDate: startDateString,
                            checkOutDate: endDateToUse,
                          });
                        }
                      }}
                    />
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup className="mb-3">
                    <Label className="fw-medium">Check-out Date *</Label>
                    <Flatpickr
                      className="form-control d-block"
                      options={{
                        dateFormat: "d M, Y",
                        minDate: createDate || undefined,
                        disableMobile: true,
                      }}
                      value={createEndDate ? new Date(createEndDate + "T00:00:00") : ""}
                      onChange={(dates) => {
                        if (dates.length > 0) {
                          const d = dates[0];
                          const endDateString =
                            d.getFullYear() +
                            "-" +
                            String(d.getMonth() + 1).padStart(2, "0") +
                            "-" +
                            String(d.getDate()).padStart(2, "0");
                          setCreateEndDate(endDateString);

                          // Refetch farms for the updated checkout date (time-slot)
                          if (createDate) {
                            fetchAvailableFarmsWithTimeFilters({
                              checkInDate: createDate,
                              checkOutDate: endDateString,
                            });
                          }
                        }
                      }}
                    />
                  </FormGroup>
                </Col>
              </Row>
              {createDate && createEndDate && (
                <small className="text-muted mt-1 d-block mb-3">
                  <i className="ri-calendar-line me-1"></i>
                  {createDate === createEndDate
                    ? "Single day booking"
                    : `${new Date(createDate).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })} to ${new Date(createEndDate).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}`}
                  {createTimeFrom && createTimeTo && (() => {
                    try {
                      const start = new Date(`${createDate}T${createTimeFrom}`);
                      const end = new Date(`${createEndDate}T${createTimeTo}`);
                      const durationMs = end - start;
                      const totalHours = durationMs > 0 ? (durationMs / (1000 * 60 * 60)) : 0;
                      return totalHours > 0 ? (
                        <span className="ms-1 fw-medium text-primary">
                          = {totalHours % 1 === 0 ? totalHours : totalHours.toFixed(1)} hour{totalHours !== 1 ? "s" : ""}
                        </span>
                      ) : null;
                    } catch {
                      return null;
                    }
                  })()}
                </small>
              )}

              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Row>
                  <Col md={6}>
                    <FormGroup className="mb-3">
                      <Label className="fw-medium">Check-in Time *</Label>
                      <div className="position-relative">
                        <Input
                          innerRef={checkInButtonRef}
                          type="text"
                          className="bg-white pe-5"
                          value={formatTime(createTimeFrom)}
                          readOnly
                          onClick={() => {
                            setCheckOutPickerOpen(false);
                            setCheckInPickerOpen((prev) => !prev);
                          }}
                        />
                        <i
                          className="ri-time-line text-muted position-absolute"
                          style={{ right: "12px", top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}
                        ></i>
                      </div>
                      <DesktopTimePicker
                        open={checkInPickerOpen}
                        onOpen={() => setCheckOutPickerOpen(false)}
                        onClose={() => setCheckInPickerOpen(false)}
                        value={createTimeFrom ? dayjs(`2000-01-01T${createTimeFrom}`) : null}
                        enableAccessibleFieldDOMStructure={false}
                        closeOnSelect
                        ampm
                        minutesStep={1}
                        onChange={(value) => {
                          if (!value || !value.isValid()) return;
                          const newTimeFrom = value.format("HH:mm");
                          setCreateTimeFrom(newTimeFrom);
                          fetchAvailableFarmsWithTimeFilters({
                            checkInFrom: newTimeFrom,
                          });
                          setCheckInPickerOpen(false);
                        }}
                        slotProps={{
                          textField: {
                            sx: { display: "none" },
                          },
                          popper: {
                            anchorEl: checkInButtonRef.current,
                            sx: {
                              "& .MuiPaper-root, & .MuiPickersLayout-root, & .MuiMultiSectionDigitalClock-root, & .MuiMultiSectionDigitalClockSection-root, & .MuiDialogActions-root": {
                                backgroundColor: "#fff",
                              },
                            },
                          },
                          actionBar: {
                            actions: ["cancel", "accept"],
                          },
                        }}
                      />
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup className="mb-3">
                      <Label className="fw-medium">Check-out Time *</Label>
                      <div className="position-relative">
                        <Input
                          innerRef={checkOutButtonRef}
                          type="text"
                          className="bg-white pe-5"
                          value={formatTime(createTimeTo)}
                          readOnly
                          onClick={() => {
                            setCheckInPickerOpen(false);
                            setCheckOutPickerOpen((prev) => !prev);
                          }}
                        />
                        <i
                          className="ri-time-line text-muted position-absolute"
                          style={{ right: "12px", top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}
                        ></i>
                      </div>
                      <DesktopTimePicker
                        open={checkOutPickerOpen}
                        onOpen={() => setCheckInPickerOpen(false)}
                        onClose={() => setCheckOutPickerOpen(false)}
                        value={createTimeTo ? dayjs(`2000-01-01T${createTimeTo}`) : null}
                        enableAccessibleFieldDOMStructure={false}
                        closeOnSelect
                        ampm
                        minutesStep={1}
                        onChange={(value) => {
                          if (!value || !value.isValid()) return;
                          const newTimeTo = value.format("HH:mm");
                          setCreateTimeTo(newTimeTo);
                          fetchAvailableFarmsWithTimeFilters({
                            checkOutTo: newTimeTo,
                          });
                          setCheckOutPickerOpen(false);
                        }}
                        slotProps={{
                          textField: {
                            sx: { display: "none" },
                          },
                          popper: {
                            anchorEl: checkOutButtonRef.current,
                            sx: {
                              "& .MuiPaper-root, & .MuiPickersLayout-root, & .MuiMultiSectionDigitalClock-root, & .MuiMultiSectionDigitalClockSection-root, & .MuiDialogActions-root": {
                                backgroundColor: "#fff",
                              },
                            },
                          },
                          actionBar: {
                            actions: ["cancel", "accept"],
                          },
                        }}
                      />
                    </FormGroup>
                  </Col>
                </Row>
              </LocalizationProvider>

              <FormGroup className="mb-3">
                <Label className="fw-medium">Select Farmhouse *</Label>
                {availableFarmhouses.length === 0 ? (
                  <div className="alert alert-warning">
                    <i className="ri-alert-line me-2"></i>
                    No farmhouses available for selected date
                  </div>
                ) : (
                  <div className="border border-primary-subtle mt-3 p-2" style={{ borderRadius: "0.375rem" }}>
                    {availableFarmhouses.map((farm, index) => (
                      <div
                        key={farm.id}
                        className={`p-3 cursor-pointer ${selectedFarmhouseId === farm.id ? "bg-primary-subtle border-primary" : ""} 
                          ${index === availableFarmhouses.length - 1 ? "" : "border-bottom"}`}
                        onClick={() => {
                          setCheckInPickerOpen(false);
                          setCheckOutPickerOpen(false);
                          setSelectedFarmhouseId(farm.id);
                        }}
                        style={{
                          cursor: "pointer",
                          transition: "all 0.2s ease",
                        }}
                      >
                        <div className="d-flex justify-content-between align-items-start">
                          <div className="d-flex align-items-start">
                            <div className="me-3">
                              <div className={`form-check form-check-lg ${selectedFarmhouseId === farm.id ? "form-check-primary" : ""}`}>
                                <input
                                  type="radio"
                                  className="form-check-input"
                                  name="farmhouseSelection"
                                  id={`farm-${farm.id}`}
                                  checked={selectedFarmhouseId === farm.id}
                                  onChange={() => {
                                    setCheckInPickerOpen(false);
                                    setCheckOutPickerOpen(false);
                                    setSelectedFarmhouseId(farm.id);
                                  }}
                                />
                              </div>
                            </div>
                            <div className="flex-grow-1 min-w-0">
                              <div className="fw-medium fs-16">{farm.name}</div>
                              <div className="d-flex align-items-center mt-1 flex-wrap gap-2">
                                {farm.farmNo && (
                                  <Badge color="info" className="fw-bold">
                                    <i className="ri-home-3-line me-1"></i> {`#${farm.farmNo}`}
                                  </Badge>
                                )}
                                <Badge color="success" className="fw-bold">
                                  <i className="ri-money-dollar-circle-line me-1"></i>
                                  From ₹
                                  {(() => {
                                    const typeForList = createDate ? (isWeekend(createDate) ? "WEEKEND_12HR" : "REGULAR_12HR") : "REGULAR_12HR";
                                    const priceOption = farm.priceOptions?.find((option) => option.category === typeForList);
                                    return priceOption?.price || farm.price || 0;
                                  })()}
                                </Badge>
                                {(() => {
                                  const typeForList = createDate ? (isWeekend(createDate) ? "WEEKEND_12HR" : "REGULAR_12HR") : "REGULAR_12HR";
                                  const priceOption = farm.priceOptions?.find((option) => option.category === typeForList);
                                  const maxPersons = priceOption?.maxPeople || farm.maxPersons || "N/A";
                                  const extraPrice = priceOption?.extraPersonPrice || 0;
                                  return (
                                    <>
                                      <Badge color="warning" className="fw-bold">
                                        <i className="ri-group-line me-1"></i> Max: {maxPersons} persons
                                      </Badge>
                                      {extraPrice > 0 && (
                                        <Badge color="soft-danger" className="text-danger fw-bold">
                                          <i className="ri-user-add-line me-1"></i>
                                          Extra: ₹{extraPrice}/person
                                        </Badge>
                                      )}
                                    </>
                                  );
                                })()}
                              </div>
                              {farm.location && (
                                <div className="text-muted mt-2">
                                  <i className="ri-map-pin-line me-1"></i>
                                  {farm.location?.city || farm.location || "Location not specified"}
                                </div>
                              )}
                            </div>
                          </div>

                          {selectedFarmhouseId === farm.id && (
                            <div className="text-success">
                              <i className="ri-checkbox-circle-fill fs-18"></i>
                            </div>
                          )}
                        </div>

                        <div className="mt-2 ps-5">
                          {farm.description && (
                            <small className="text-muted d-block">
                              <i className="ri-file-text-line me-1"></i>
                              {farm.description.length > 100 ? farm.description.substring(0, 100) + "..." : farm.description}
                            </small>
                          )}

                          <div className="d-flex gap-2 mt-2">
                            {farm.amenities && farm.amenities.length > 0 && (
                              <small className="text-primary">
                                <i className="ri-star-line me-1"></i>
                                {farm.amenities.length} amenities
                              </small>
                            )}

                            {farm.images && farm.images.length > 0 && (
                              <small className="text-info">
                                <i className="ri-image-line me-1"></i>
                                {farm.images.length} photos
                              </small>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div className="d-flex justify-content-between mt-2">
                  <small className="text-muted">
                    <i className="ri-information-line me-1"></i>
                    {availableFarmhouses.length} farmhouses available
                  </small>
                  {selectedFarmhouseId && (
                    <small className="text-success fw-medium">
                      <i className="ri-check-line me-1"></i>
                      Selected
                    </small>
                  )}
                </div>
              </FormGroup>
            </div>
          )}

          {!skipUserStep && createStep === 2 && (
            <div>
              <h6 className="mb-3 fw-semibold text-primary">
                <i className="ri-user-line me-2"></i>
                Select or Create Customer
              </h6>

              <FormGroup className="mb-3">
                <Label className="fw-medium">Search Customer</Label>
                <div className="input-group">
                  <span className="input-group-text">
                    <i className="ri-search-line"></i>
                  </span>
                  <Input
                    type="text"
                    placeholder="Search by name, email or phone number"
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      fetchUsersForSelection(e.target.value);
                    }}
                    className="form-control"
                  />
                </div>
                <small className="text-muted mt-1 d-block">
                  <i className="ri-information-line me-1"></i>
                  Search for existing customers or create new one
                </small>
              </FormGroup>

              <div className="mb-3">
                <Label className="fw-medium">Select Customer</Label>
                <div style={{ maxHeight: "200px", overflowY: "auto", border: "1px solid #dee2e6", borderRadius: "0.375rem" }}>
                  {users.length === 0 ? (
                    <div className="text-center py-4 text-muted">
                      <i className="ri-user-unfollow-line fs-4 mb-2 d-block"></i>
                      No customers found
                    </div>
                  ) : (
                    users.map((user) => (
                      <div
                        key={user.id}
                        className={`p-3 border-bottom cursor-pointer ${selectedUserId === user.id ? "bg-primary-subtle" : ""}`}
                        onClick={() => setSelectedUserId(user.id)}
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <div className="fw-medium">{user.name}</div>
                            <div className="text-muted small">{user.mobile || user.mobileNo || "No mobile"}</div>
                          </div>
                          {selectedUserId === user.id && <i className="ri-check-line text-success"></i>}
                        </div>
                        {user.email && <div className="small text-truncate mt-1">{user.email}</div>}
                      </div>
                    ))
                  )}
                </div>
              </div>

              <div className="text-center my-3">
                <div className="text-muted mb-2">OR</div>
                <Button color="outline-primary" onClick={() => setCreateUserModalOpen(true)} className="w-100">
                  <i className="ri-user-add-line me-2"></i>
                  Create New Customer
                </Button>
              </div>

              {selectedUserId && (
                <Card className="border border-success-subtle mt-3">
                  <CardBody className="p-3">
                    {(() => {
                      const selectedUser = users.find((user) => user.id === selectedUserId);
                      if (!selectedUser) return null;
                      return (
                        <>
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <h6 className="mb-0">
                              <i className="ri-check-line me-2 text-success"></i>
                              Selected Customer
                            </h6>
                            <Button
                              color="link"
                              size="sm"
                              className="p-0 text-primary fw-medium"
                              onClick={() => handleOpenEditUser(selectedUser)}
                              style={{ textDecoration: 'none' }}
                            >
                              <i className="ri-edit-line me-1"></i>
                              {selectedUser.mobileNo || selectedUser.mobile ? "Edit" : "Add Mobile No."}
                            </Button>
                          </div>
                          <Row className="g-2">
                            <Col xs={6}>
                              <small className="text-muted d-block">Name</small>
                              <span className="fw-medium">{selectedUser.name}</span>
                            </Col>
                            <Col xs={6}>
                              <small className="text-muted d-block">Mobile</small>
                              <span className={!(selectedUser.mobileNo) ? "text-danger italic small" : ""}>
                                {selectedUser.mobileNo || "Please add mobile"}
                              </span>
                            </Col>
                            <Col xs={12}>
                              <small className="text-muted d-block">Email</small>
                              <span>{selectedUser.email || "Not provided"}</span>
                            </Col>
                          </Row>
                        </>
                      );
                    })()}
                  </CardBody>
                </Card>
              )}
            </div>
          )}

          {((!skipUserStep && createStep === 3) || (skipUserStep && createStep === 2)) && (
            <div>
              <h6 className="mb-3 fw-semibold text-primary">
                <i className="ri-check-double-line me-2"></i>
                Confirm Booking Details
              </h6>

              <Card className="border-0 shadow-sm mb-3">
                <CardBody className="p-3">
                  <h6 className="mb-2 border-bottom pb-2">
                    <i className="ri-home-4-line me-2"></i>
                    Farmhouse Details
                  </h6>
                  {(() => {
                    const selectedFarm = availableFarmhouses.find((farm) => farm.id === bookingFormData.farmhouseId);
                    if (!selectedFarm) return null;

                    const priceOption = selectedFarm.priceOptions?.find((option) => option.category === bookingFormData.bookingType);
                    const price = priceOption?.price || selectedFarm.price || 0;
                    const maxPersonsForType = priceOption?.maxPeople || selectedFarm.maxPersons || 10;
                    const extraPersonPrice = bookingFormData.extraPersonPrice || priceOption?.extraPersonPrice || 0;

                    return (
                      <Row className="g-2">
                        <Col xs={6}>
                          <small className="text-muted d-block">Name</small>
                          <span className="fw-medium">{selectedFarm.name}</span>
                        </Col>
                        <Col xs={6}>
                          <small className="text-muted d-block">Farm No</small>
                          {selectedFarm.farmNo ? (
                            <Badge color="info" className="fw-bold">
                              {`#${selectedFarm.farmNo}`}
                            </Badge>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </Col>
                        <Col xs={6}>
                          <small className="text-muted d-block">Maximum Capacity</small>
                          <span className="fw-medium text-primary">{maxPersonsForType} persons</span>
                        </Col>
                        <Col xs={6}>
                          <small className="text-muted d-block">Location</small>
                          <span>{selectedFarm.location?.city || selectedFarm.location || "N/A"}</span>
                        </Col>
                        <Col xs={6}>
                          <small className="text-muted d-block">Price for {getBookingTypeLabel(bookingFormData.bookingType)}</small>
                          <span className="fw-bold" style={{ color: '#00209f' }}>₹{price}</span>
                        </Col>
                        <Col xs={6}>
                          <small className="text-muted d-block">Extra Person Price</small>
                          <span className="fw-medium">{extraPersonPrice > 0 ? `₹${extraPersonPrice}/person` : "Free"}</span>
                        </Col>
                        <Col xs={6}>
                          <small className="text-muted d-block">Bedrooms</small>
                          <span>{selectedFarm.bedrooms || "N/A"}</span>
                        </Col>
                      </Row>
                    );
                  })()}
                </CardBody>
              </Card>

              <Card className="border-0 shadow-sm mb-3">
                <CardBody className="p-3">
                  <h6 className="mb-2 border-bottom pb-2">
                    <i className="ri-user-line me-2"></i>
                    Customer Details
                  </h6>
                  {renderSelectedCustomer ? (
                    <Row className="g-2">
                      <Col xs={6}>
                        <small className="text-muted d-block">Name</small>
                        <span className="fw-medium">{renderSelectedCustomer.name}</span>
                      </Col>
                      <Col xs={6}>
                        <small className="text-muted d-block">Mobile</small>
                        <span>{renderSelectedCustomer.mobileNo || renderSelectedCustomer.mobile || "N/A"}</span>
                      </Col>
                      <Col xs={12}>
                        <small className="text-muted d-block">Email</small>
                        <span>{renderSelectedCustomer.email || "Not provided"}</span>
                      </Col>
                    </Row>
                  ) : (
                    <div className="alert alert-warning mb-0">No customer selected.</div>
                  )}
                </CardBody>
              </Card>

              <Card className="border-0 shadow-sm">
                <CardBody className="p-3">
                  <h6 className="mb-3 border-bottom pb-2">
                    <i className="ri-money-dollar-circle-line me-2"></i>
                    Price Calculation
                  </h6>

                  <Row className="mb-3">
                    <Col xs={6}>
                      <FormGroup>
                        <Label for="originalPrice" className="fw-medium small">
                          Base Price (₹)
                        </Label>
                        <Input
                          type="number"
                          id="originalPrice"
                          value={bookingFormData.originalPrice === "" ? "" : (bookingFormData.originalPrice === 0 || bookingFormData.originalPrice == null ? "0" : bookingFormData.originalPrice)}
                          onChange={(e) => {
                            const raw = e.target.value;
                            const original = raw === "" ? "" : (parseFloat(raw) || 0);
                            const originalNum = raw === "" ? 0 : (parseFloat(raw) || 0);
                            const extra = bookingFormData.extraPersonCharge || 0;
                            const discount = bookingFormData.discountAmount || 0;
                            const paid = bookingFormData.paidAmount || 0;
                            const finalPrice = originalNum + extra - discount;
                            const remaining = Math.max(0, finalPrice - paid);

                            setBookingFormData((prev) => ({
                              ...prev,
                              originalPrice: original,
                              finalPrice: finalPrice,
                              remainingAmount: remaining,
                            }));
                          }}
                          onFocus={(e) => {
                            if (e.target.value === "0" || Number(e.target.value) === 0) {
                              setBookingFormData((prev) => ({ ...prev, originalPrice: "" }));
                            }
                          }}
                          onBlur={(e) => {
                            if (e.target.value === "") {
                              setBookingFormData((prev) => ({ ...prev, originalPrice: 0 }));
                            }
                          }}
                          min="0"
                          step="0.01"
                          className="form-control form-control-sm"
                        />
                      </FormGroup>
                    </Col>
                    <Col xs={6}>
                      <FormGroup>
                        <Label for="discountAmount" className="fw-medium small">
                          Discount (₹)
                        </Label>
                        <Input
                          type="number"
                          id="discountAmount"
                          value={bookingFormData.discountAmount === "" ? "" : (bookingFormData.discountAmount === 0 || bookingFormData.discountAmount == null ? "0" : bookingFormData.discountAmount)}
                          onChange={(e) => {
                            const raw = e.target.value;
                            const discount = raw === "" ? "" : (parseFloat(raw) || 0);
                            const discountNum = raw === "" ? 0 : (parseFloat(raw) || 0);
                            const original = bookingFormData.originalPrice || 0;
                            const extra = bookingFormData.extraPersonCharge || 0;
                            const paid = bookingFormData.paidAmount || 0;
                            const finalPrice = original + extra - discountNum;
                            const remaining = Math.max(0, finalPrice - paid);

                            setBookingFormData((prev) => ({
                              ...prev,
                              discountAmount: discount,
                              finalPrice: finalPrice,
                              remainingAmount: remaining,
                            }));
                          }}
                          onFocus={(e) => {
                            if (e.target.value === "0" || Number(e.target.value) === 0) {
                              setBookingFormData((prev) => ({ ...prev, discountAmount: "" }));
                            }
                          }}
                          onBlur={(e) => {
                            if (e.target.value === "") {
                              setBookingFormData((prev) => ({ ...prev, discountAmount: 0 }));
                            }
                          }}
                          min="0"
                          step="0.01"
                          className="form-control form-control-sm"
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  <div className="border-top pt-3">
                    {bookingFormData.extraPersonCharge > 0 && (
                      <div className="d-flex justify-content-between align-items-center mb-1 text-danger">
                        <small className="fw-medium">Extra Person Charges ({(Number(bookingFormData.numberOfPersons) || 1) - bookingFormData.farmMaxPersons} × ₹{bookingFormData.extraPersonPrice}):</small>
                        <small className="fw-bold">+₹{bookingFormData.extraPersonCharge}</small>
                      </div>
                    )}
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="fw-medium">Total Amount:</span>
                      <span className="fs-5 fw-bold" style={{ color: '#00209f' }}>₹{bookingFormData.finalPrice || 0}</span>
                    </div>
                    <small className="text-muted d-block mt-1">
                      <i className="ri-information-line me-1"></i>
                      {bookingFormData.discountAmount > 0
                        ? `₹${bookingFormData.originalPrice || 0} ${bookingFormData.extraPersonCharge > 0 ? `+ ₹${bookingFormData.extraPersonCharge}` : ''} - ₹${bookingFormData.discountAmount || 0} discount`
                        : bookingFormData.extraPersonCharge > 0
                          ? `₹${bookingFormData.originalPrice || 0} + ₹${bookingFormData.extraPersonCharge} extra person charge`
                          : "No discount applied"}
                    </small>
                  </div>
                </CardBody>
              </Card>

              <Card className="border-0 shadow-sm mt-3">
                <CardBody className="p-3">
                  <h6 className="mb-3 border-bottom pb-2">
                    <i className="ri-bank-card-line me-2"></i>
                    Payment Details
                  </h6>

                  <Row className="mb-2">
                    <Col xs={6}>
                      <FormGroup className="mb-2">
                        <Label for="paymentStatus" className="fw-medium small">
                          Payment Status
                        </Label>
                        <Input
                          type="select"
                          id="paymentStatus"
                          value={bookingFormData.paymentStatus || "paid"}
                          onChange={(e) => {
                            const newStatus = e.target.value;
                            const total = bookingFormData.finalPrice || 0;
                            let paid = bookingFormData.paidAmount || 0;
                            let remaining = bookingFormData.remainingAmount || total;

                            if (newStatus === "paid") {
                              paid = total;
                              remaining = 0;
                            } else if (newStatus === "incomplete") {
                              paid = 0;
                              remaining = total;
                            } else if (newStatus === "partial") {
                              paid = Math.floor(total / 2);
                              remaining = total - paid;
                            }

                            setBookingFormData((prev) => ({
                              ...prev,
                              paymentStatus: newStatus,
                              paidAmount: paid,
                              remainingAmount: remaining,
                            }));
                          }}
                          className="form-control form-control-sm"
                          style={{ fontSize: "12px" }}
                        >
                          <option value="incomplete">Not Paid</option>
                          <option value="partial">Outstanding</option>
                          <option value="paid">Paid</option>
                        </Input>
                      </FormGroup>
                    </Col>
                    <Col xs={6}>
                      <FormGroup className="mb-2">
                        <Label for="numberOfPersons" className="fw-medium small">
                          Number of Persons
                          {(() => {
                            const selectedFarm = availableFarmhouses.find((farm) => farm.id === bookingFormData.farmhouseId);
                            if (selectedFarm) {
                              const priceOption = selectedFarm.priceOptions?.find(
                                (option) => option.category === bookingFormData.bookingType
                              );
                              const maxPersonsForType = priceOption?.maxPeople || selectedFarm.maxPersons || 10;
                              return (
                                <small className="text-muted ms-1" style={{ fontSize: '10px' }}>
                                  (Max: {maxPersonsForType})
                                </small>
                              );
                            }
                            return null;
                          })()}
                        </Label>
                        <Input
                          type="number"
                          id="numberOfPersons"
                          value={bookingFormData.numberOfPersons === "" ? "" : (bookingFormData.numberOfPersons === 0 || bookingFormData.numberOfPersons == null ? "1" : bookingFormData.numberOfPersons)}
                          style={{ height: "35px" }}
                          onFocus={(e) => {
                            if (e.target.value === "1" || e.target.value === "0" || Number(e.target.value) === 1) {
                              setBookingFormData((prev) => ({ ...prev, numberOfPersons: "" }));
                            }
                          }}
                          onChange={(e) => {
                            const raw = e.target.value;
                            const persons = raw === "" ? "" : (parseInt(raw, 10) || 0);
                            const personsNum = raw === "" ? 1 : (parseInt(raw, 10) || 1);
                            const maxPersonsForType = bookingFormData.farmMaxPersons || 10;
                            const extraPersonPrice = bookingFormData.extraPersonPrice || 0;

                            let extraCharge = 0;
                            if (personsNum > maxPersonsForType) {
                              extraCharge = (personsNum - maxPersonsForType) * extraPersonPrice;
                            }

                            const original = bookingFormData.originalPrice || 0;
                            const discount = bookingFormData.discountAmount || 0;
                            const finalPrice = original + extraCharge - discount;

                            let paid = bookingFormData.paidAmount || 0;
                            if (bookingFormData.paymentStatus === "paid") {
                              paid = finalPrice;
                            }
                            const remaining = Math.max(0, finalPrice - paid);

                            setBookingFormData((prev) => ({
                              ...prev,
                              numberOfPersons: persons,
                              extraPersonCharge: extraCharge,
                              finalPrice: finalPrice,
                              paidAmount: paid,
                              remainingAmount: remaining,
                            }));
                          }}
                          onBlur={(e) => {
                            if (e.target.value === "") {
                              setBookingFormData((prev) => ({ ...prev, numberOfPersons: 1 }));
                            }
                          }}
                          min="1"
                          className="form-control form-control-sm"
                        />
                        {(Number(bookingFormData.numberOfPersons) || 1) > bookingFormData.farmMaxPersons && (
                          <div className="text-muted mt-1 ps-1" style={{ fontSize: '9px', opacity: 0.8, fontWeight: '500' }}>
                            Calculation: {(Number(bookingFormData.numberOfPersons) || 1) - bookingFormData.farmMaxPersons} Extra × ₹{bookingFormData.extraPersonPrice} = ₹{bookingFormData.extraPersonCharge}
                          </div>
                        )}
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row>
                    <Col xs={6}>
                      <FormGroup className="mb-0">
                        <Label for="paidAmount" className="fw-medium small">
                          Paid Amount (₹)
                          <small className="text-muted ms-1">(Editable)</small>
                        </Label>
                        <Input
                          type="number"
                          id="paidAmount"
                          value={bookingFormData.paidAmount === "" ? "" : (bookingFormData.paidAmount === 0 || bookingFormData.paidAmount == null ? "0" : bookingFormData.paidAmount)}
                          onChange={(e) => {
                            const raw = e.target.value;
                            const paid = raw === "" ? "" : (parseFloat(raw) || 0);
                            const paidNum = raw === "" ? 0 : (parseFloat(raw) || 0);
                            const total = bookingFormData.finalPrice || 0;
                            const remaining = Math.max(0, total - paidNum);

                            let newStatus = bookingFormData.paymentStatus;
                            if (paidNum === 0) {
                              newStatus = "incomplete";
                            } else if (paidNum > 0 && paidNum < total) {
                              newStatus = "partial";
                            } else if (paidNum >= total) {
                              newStatus = "paid";
                            }

                            setBookingFormData((prev) => ({
                              ...prev,
                              paidAmount: paid,
                              remainingAmount: remaining,
                              paymentStatus: newStatus,
                            }));
                          }}
                          onFocus={(e) => {
                            if (e.target.value === "0" || Number(e.target.value) === 0) {
                              setBookingFormData((prev) => ({ ...prev, paidAmount: "" }));
                            }
                          }}
                          onBlur={(e) => {
                            if (e.target.value === "") {
                              setBookingFormData((prev) => ({ ...prev, paidAmount: 0 }));
                            }
                          }}
                          min="0"
                          step="0.01"
                          max={bookingFormData.finalPrice || 0}
                          className="form-control form-control-sm"
                          placeholder="Enter paid amount"
                        />
                      </FormGroup>
                    </Col>

                    <Col xs={6}>
                      <FormGroup className="mb-0">
                        <Label for="remainingAmount" className="fw-medium small">
                          Remaining Amount (₹)
                          <small className="text-muted ms-1">(Auto-calculated)</small>
                        </Label>
                        <Input
                          type="number"
                          id="remainingAmount"
                          value={bookingFormData.remainingAmount || 0}
                          readOnly
                          className="form-control form-control-sm"
                          style={{ backgroundColor: '#f5f5f5' }}
                          placeholder="Remaining amount"
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </div>
          )}
        </ModalBody>

        <ModalFooter>
          <div className="d-flex justify-content-between w-100">
            {createStep > 1 ? (
              <Button
                color="light"
                onClick={() => setCreateStep((prev) => Math.max(1, prev - 1))}
                disabled={submitting}
              >
                <i className="ri-arrow-left-line me-1"></i>
                Back
              </Button>
            ) : (
              <div></div>
            )}

            {((!skipUserStep && createStep < 3) || (skipUserStep && createStep < 2)) ? (
              <Button
                color="primary"
                onClick={createStep === 1 ? handleStep1Next : handleStep2Next}
                disabled={submitting}
              >
                Next <i className="ri-arrow-right-line ms-1"></i>
              </Button>
            ) : (
              <Button color="primary" onClick={handleCreateBooking} disabled={submitting}>
                {submitting ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Creating...
                  </>
                ) : (
                  <>
                    <i className="ri-check-line me-1"></i>
                    Create Booking
                  </>
                )}
              </Button>
            )}
          </div>
        </ModalFooter>
      </Modal >

      <Modal isOpen={createUserModalOpen} toggle={() => setCreateUserModalOpen(false)} centered>
        <ModalHeader toggle={() => setCreateUserModalOpen(false)}>
          <i className="ri-user-add-line me-2"></i>
          Create New Customer
        </ModalHeader>
        <ModalBody>
          <div className="mb-3">
            <label className="form-label fw-medium">Name *</label>
            <Input
              type="text"
              placeholder="Enter full name"
              value={newUser.name}
              onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
              className="form-control"
            />
          </div>
          <div className="mb-3">
            <label className="form-label fw-medium">Email</label>
            <Input
              type="email"
              placeholder="Enter email"
              value={newUser.email}
              onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
              className="form-control"
            />
          </div>
          <div className="text-center my-2">
            <div className="text-muted">OR</div>
          </div>
          <div className="mb-3">
            <label className="form-label fw-medium">Mobile Number</label>
            <Input
              type="text"
              placeholder="Enter mobile number"
              value={newUser.mobileNo}
              onChange={(e) => setNewUser({ ...newUser, mobileNo: e.target.value })}
              className="form-control"
            />
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setCreateUserModalOpen(false)} disabled={creatingUser}>
            Cancel
          </Button>
          <Button color="primary" onClick={handleCreateNewUser} disabled={creatingUser}>
            {creatingUser ? (
              <>
                <Spinner size="sm" className="me-2" />
                Creating...
              </>
            ) : (
              <>
                <i className="ri-check-line me-1"></i>
                Create Customer
              </>
            )}
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={editUserModalOpen} toggle={() => setEditUserModalOpen(false)} centered>
        <ModalHeader toggle={() => setEditUserModalOpen(false)}>
          <i className="ri-user-settings-line me-2"></i>
          Update Customer Details
        </ModalHeader>
        <ModalBody>
          <div className="mb-3">
            <label className="form-label fw-medium">Name *</label>
            <Input
              type="text"
              placeholder="Enter full name"
              value={editUserData.name}
              onChange={(e) => setEditUserData({ ...editUserData, name: e.target.value })}
              className="form-control"
            />
          </div>
          <div className="mb-3">
            <label className="form-label fw-medium">Email</label>
            <Input
              type="email"
              placeholder="Enter email"
              value={editUserData.email}
              onChange={(e) => setEditUserData({ ...editUserData, email: e.target.value })}
              className="form-control"
            />
          </div>
          <div className="mb-3">
            <label className="form-label fw-medium">Mobile Number</label>
            <Input
              type="text"
              placeholder="Enter mobile number"
              value={editUserData.mobileNo}
              onChange={(e) => setEditUserData({ ...editUserData, mobileNo: e.target.value })}
              className="form-control"
            />
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setEditUserModalOpen(false)} disabled={updatingUser}>
            Cancel
          </Button>
          <Button color="primary" onClick={handleUpdateUser} disabled={updatingUser}>
            {updatingUser ? (
              <>
                <Spinner size="sm" className="me-2" />
                Updating...
              </>
            ) : (
              <>
                <i className="ri-check-line me-1"></i>
                Update Details
              </>
            )}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default CreateBookingModal;